import React, { useState } from 'react';
import { notification, Space, Tag } from 'antd';
import type { TablePaginationConfig, TableProps } from 'antd';
import {
  IPriceSales,
  ISearchPriceSalesReq,
  ISearchPriceSalesRes,
} from '@/interfaces/price-sales';
import { EditFilled, DeleteFilled, UploadOutlined } from '@ant-design/icons';
import { deletePriceSales } from '@/adapters/price-sales';
import { IDataModal } from '@/interfaces/dataModal';
import DeleteConfirmModal from '@/components/modal/confirm-delete';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import TableComponent from '@/components/table';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import { twMerge } from 'tailwind-merge';
import { mediaView } from '@/utils/mediaUtils';
import dayjs from 'dayjs';

interface IProps {
  loading: boolean;
  criteria: ISearchPriceSalesReq;
  priceSales: ISearchPriceSalesRes;
  onDelete: (id: number) => void;
  onEditData: (item: IPriceSales) => void;
  onTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IPriceSales> | SorterResult<IPriceSales>[],
  ) => void;
  onUploadQuotation: (id: number) => void;
}

const _PriceSalesModal: IDataModal<IPriceSales> = {
  open: false,
};

const PriceSalesTable = ({
  loading,
  criteria,
  priceSales,
  onDelete,
  onEditData,
  onTableChange,
  onUploadQuotation,
}: IProps) => {
  const [deleteModal, setDeleteModal] =
    useState<IDataModal<IPriceSales>>(_PriceSalesModal);

  const onDeletePriceSales = async () => {
    try {
      await deletePriceSales(Number(deleteModal?.id));
      notification.success({
        message: 'ลบ Price Sales สำเร็จ',
      });
      onDelete(Number(deleteModal?.id || 0));
      setDeleteModal({ ..._PriceSalesModal });
    } catch (error) {
      console.log(error);
    }
  };

  const columns: TableProps<IPriceSales>['columns'] = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 40,
      align: 'center',
      className: 'text-center',
      render: (_, rec, i) => <Space>{i + 1}</Space>,
    },
    {
      title: 'รหัสสินค้า',
      dataIndex: 'productCode',
      key: 'productCode',
      align: 'center',
      width: 140,
    },
    {
      title: 'ชื่อสินค้า',
      dataIndex: 'productName',
      key: 'productName',
    },
    {
      title: 'เลขที่ใบเสนอราคา',
      dataIndex: 'qtNo',
      key: 'qtNo',
      align: 'center',
      width: 160,
      render: (_, rec) => (
        <div className="flex flex-row gap-3 cursor-pointer justify-between">
          <div
            className={twMerge(
              !rec.qtNo && 'text-gray-400',
              rec.qtId && 'text-blue-600 underline',
            )}
            onClick={() => (rec.qtId ? mediaView({ id: rec.qtId }) : false)}
          >
            {_ ?? 'ไม่พบใบเสนอราคา'}
          </div>
          {rec.qtNo && (
            <div
              className="font-bold"
              onClick={() => onUploadQuotation(rec.id)}
            >
              <UploadOutlined />
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Valid From',
      dataIndex: 'validFrom',
      key: 'validFrom',
      width: 120,
      align: 'center',
      render: _ => formatDate(_),
    },
    {
      title: 'Vaid To',
      dataIndex: 'validTo',
      key: 'validTo',
      width: 120,
      align: 'center',
      // className: twMerge( 'line-through text-red-500'),
      render: _ => {
        const isExpired = _ && dayjs(_).isBefore(dayjs(), 'day');
        return (
          <div className={twMerge(isExpired && 'text-red-500')}>
            {`${formatDate(_)}`}
            {isExpired && ' (Expired)'}
          </div>
        );
      },
    },
    {
      title: 'Base Unit Price',
      dataIndex: 'productUnitPrice',
      key: 'productUnitPrice',
      className: 'font-bold ',
      align: 'right',
      width: 120,
      render: _ => formatNumber(_, 2),
    },
    {
      title: 'ราคาขาย',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      align: 'right',
      width: 120,
      render: (_: number, rec) => (
        <div
          className={twMerge(
            'font-bold text-green-600',
            Number(rec.productUnitPrice) > _ && 'text-orange-500',
          )}
        >
          {formatNumber(_, 2)}
        </div>
      ),
    },
    {
      title: 'อัพเดทโดย',
      dataIndex: 'updateByName',
      key: 'updateByName',
      width: 160,
    },
    {
      title: 'อัพเดทล่าสุด',
      dataIndex: 'updateDate',
      key: 'updateDate',
      width: 120,
      align: 'center',
      render: _ => formatDate(_),
    },
    {
      title: 'สถานะ',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      align: 'center',
      render: (_: boolean) => {
        return (
          <Tag color={_ ? `green` : 'red'} className="min-w-[60px] text-center">
            {_ ? 'ใช้งานอยู่' : 'ปิดงานใข้งาน'}
          </Tag>
        );
      },
    },
    {
      dataIndex: 'action',
      key: 'action',
      width: 80,
      render: (_, rec) => (
        <div className="flex flex-row gap-4 items-center w-full justify-center">
          <EditFilled
            className="text-blue-500"
            onClick={() => onEditData(rec)}
          />
          <DeleteFilled
            className="text-red-700"
            onClick={() =>
              setDeleteModal({
                open: true,
                item: rec,
                id: rec.id,
              })
            }
          />
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<IPriceSales>
        columns={columns}
        criteria={criteria}
        data={priceSales}
        loading={loading}
        onTableChange={onTableChange}
        showPagination={true}
        scrollLength={1600}
      />
      <DeleteConfirmModal
        content={`คุณต้องการลบ '${deleteModal?.item?.productName}' ใช่หรือไม่`}
        handleCancel={() => {
          setDeleteModal({ ..._PriceSalesModal });
        }}
        handleOk={() => {
          onDeletePriceSales();
        }}
        open={deleteModal?.open}
        title={`Price Sales '${deleteModal?.item?.productName}'`}
      />
    </div>
  );
};

export default PriceSalesTable;
